/**
 * The share dialogs' and the share toolbar's hook onto the ONE renderer reading of the Files gate
 * (`../fileGates.ts` — `fileActionReason`: read-only → §3.7 attachment rule → `fileOperationGate`, the tables main and
 * the MCP pre-approval check read). The hooks subscribe to the connection record so a changed scope or read-only flag
 * re-gates the open surface.
 */
import { useCallback } from 'react';
import type { FileShareOperation } from '../../../../../types/azureStorage';
import type { Connection } from '../../../../../types/electron';
import { useConnectionStore } from '../../../../connections/store/connectionStore';
import { fileActionReason, type FileGateContext } from '../fileGates';

/** The connection record (saved, else the live one); re-renders when it changes. */
export function useShareConnection(connectionId: string): Connection | undefined {
  const saved = useConnectionStore((state) => state.connections.find((connection) => connection.id === connectionId));
  const active = useConnectionStore((state) => state.activeConnections.find((entry) => entry.id === connectionId)?.connection);
  return saved ?? active;
}

export type ShareRefusal = (op: FileShareOperation, context?: FileGateContext) => string | undefined;

/** `refuse(op, { protocol, snapshot })` → the disabled reason, or undefined when the control is offered. `readOnly`
 *  adds a surface's own read-only fact (the toolbar context's) to the connection's. */
export function useShareRefusal(connectionId: string, readOnly = false): ShareRefusal {
  const connection = useShareConnection(connectionId);
  return useCallback(
    (op, context = {}) => fileActionReason(connection && readOnly ? { ...connection, readOnly: true } : connection, op, context),
    [connection, readOnly],
  );
}
